import { discountCodes } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Response schema — omit internal audit fields; clients don't need DB metadata.
export const selectDiscountCodeSchema = createSelectSchema(discountCodes).omit({
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const insertDiscountCodeSchema = createInsertSchema(discountCodes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// A discount code enriched with usage stats for the admin list view.
// visitCount = non-cancelled visits that used the code; revenue = gross service
// revenue of those visits; totalDiscount = total discount given (gross).
export const discountCodeWithStatsSchema = selectDiscountCodeSchema.extend({
  visitCount: z.number(),
  revenue: z.number(),
  totalDiscount: z.number(),
  // Minted by the huoltosuositus automation for one customer, as opposed to
  // a campaign code typed in by an admin.
  personal: z.boolean(),
})

export type DiscountCode = z.infer<typeof selectDiscountCodeSchema>
export type CreateDiscountCode = z.infer<typeof insertDiscountCodeSchema>
export type DiscountCodeWithStats = z.infer<typeof discountCodeWithStatsSchema>

export const thankYouVoucherStatsInputSchema = z.object({
  days: z.number().int().positive().default(30),
})

export const thankYouVoucherStatsSchema = z.object({
  sent: z.number(),
  opened: z.number(),
  redeemed: z.number(),
  // What the customer pays for the visits booked with a voucher (gross).
  revenue: z.number(),
  // Which service each redeemed voucher was spent on, by display name.
  redeemedByService: z.array(
    z.object({ service: z.string(), count: z.number() }),
  ),
})

export type ThankYouVoucherStats = z.infer<typeof thankYouVoucherStatsSchema>

// One sent thank-you mail: the visit it thanked for and, once used, the visit
// the voucher paid for.
export const thankYouVoucherRowSchema = z.object({
  notificationId: z.number(),
  sentAt: z.string(),
  openedAt: z.string().nullable(),
  sourceVisitId: z.number(),
  sourceService: z.string(),
  customerId: z.number().nullable(),
  customerName: z.string().nullable(),
  city: z.string().nullable(),
  code: z.string().nullable(),
  redemption: z
    .object({
      visitId: z.number(),
      startTime: z.string().nullable(),
      service: z.string(),
      // Gross, as in the stats revenue.
      revenue: z.number(),
    })
    .nullable(),
})

export type ThankYouVoucherRow = z.infer<typeof thankYouVoucherRowSchema>

// A thank-you voucher as the campaign page shows it: values per booking-widget
// service and the expiry in the same display form as the email. Only the
// status for a code that isn't redeemable. validUntil is the exact instant,
// for a page that counts down to it (the booking-SMS discount).
export const voucherDetailsSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('valid'),
    values: z.record(z.string(), z.number()),
    expiresAt: z.string(),
    validUntil: z.string().nullable(),
  }),
  z.object({ status: z.literal('expired'), expiresAt: z.string() }),
  z.object({ status: z.literal('used') }),
  z.object({ status: z.literal('invalid') }),
])

export type VoucherDetails = z.infer<typeof voucherDetailsSchema>
